import DraftIcon from '@/assets/icons/draft-icon';
import { NavChart } from '@/assets/icons/nav-chart';
import { NavCustomer } from '@/assets/icons/nav-customer';
import { NavHome } from '@/assets/icons/nav-home';
import { NavTask } from '@/assets/icons/nav-task';
import { OrderIcon } from '@/assets/icons/order-icon';
import { QuoteIcon } from '@/assets/icons/quote-icon';
import TransactionIcon from '@/assets/icons/transaction-icon';
import { useAppConfig } from '@/features/acl/acl-provider';
import { useAppState } from '@/lib/hooks/use-app-state';
import { useLocale } from '@/lib/hooks/use-locale';
import type { CrmTaskFilterOptions } from '@/server/dto/crm-task-dto';
import { api } from '@/utils/api';
import { CrmTaskStatus } from '@plato/data/prisma';
import { endOfDay } from 'date-fns';
import { Calendar } from 'lucide-react';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { MenuItem, MenuSubItem } from '../contracts';
import { NavPathOverrideEnum } from '../contracts';

type RawMenuItem = Omit<MenuItem, 'subItems'> & {
    subItems?: Array<MenuSubItem | undefined | false>;
};

export const usePlatoLayoutNavigation = () => {
    const { platoUser } = useAppState();
    const { t } = useLocale();
    const location = useLocation();

    const { canReadOrders, canReadQuotes, canManageTransactionDraft, enableCustomerEventPlanning } =
        useAppConfig();

    const { data: taskCount } = api.platoUser.query.tasks.getManyTasks.count.useQuery(
        {
            customerIds: [],
            assignedToIds: [platoUser?.id || ''],
            assignedByIds: [],
            status: CrmTaskStatus.OPEN,
            dueDate: [
                {
                    direction: 'LT',
                    timestamp: endOfDay(new Date()).toISOString(),
                },
            ],
        } satisfies CrmTaskFilterOptions,
        {
            enabled: !!platoUser?.id,
            refetchInterval: 60 * 1000,
        },
    );

    const navigation: MenuItem[] = useMemo(() => {
        if (!platoUser) return [];

        const items: Array<RawMenuItem | undefined | false> = [
            {
                id: NavPathOverrideEnum.HOME,
                name: t('page_title__home'),
                href: '/home',
                icon: NavHome,
            },
            {
                id: NavPathOverrideEnum.CUSTOMERS,
                name: t('page_title__customers'),
                href: '/customers',
                icon: NavCustomer,
            },
            {
                id: NavPathOverrideEnum.TRANSACTIONS,
                name: t('page_title__transactions'),
                icon: TransactionIcon,
                subItems: [
                    canReadQuotes && {
                        id: NavPathOverrideEnum.SALES_QUOTES,
                        name: t('page_title__sales_quotes'),
                        href: '/quotes',
                        icon: QuoteIcon,
                    },
                    canReadOrders && {
                        id: NavPathOverrideEnum.SALES_ORDERS,
                        name: t('page_title__sales_orders'),
                        href: '/orders',
                        icon: OrderIcon,
                    },
                    canManageTransactionDraft && {
                        id: NavPathOverrideEnum.SALES_DRAFTS,
                        name: t('page_title__sales_drafts'),
                        href: '/drafts',
                        icon: DraftIcon,
                    },
                ],
            },
            {
                id: NavPathOverrideEnum.EXPLORER,
                name: t('page_title__explorer'),
                href: '/explorer',
                icon: NavChart,
            },
            {
                id: NavPathOverrideEnum.TASKS,
                name: t('page_title__tasks'),
                href: '/tasks',
                icon: NavTask,
                count: taskCount,
            },
            enableCustomerEventPlanning && {
                id: NavPathOverrideEnum.EVENTS,
                name: t('page_title__events'),
                href: '/events',
                icon: Calendar,
            },
        ];

        return items
            .map((item): MenuItem | undefined => {
                if (!item) {
                    return undefined;
                }

                if (!item.subItems) {
                    return item as MenuItem;
                }

                const subItems = item.subItems.filter((i): i is MenuSubItem => Boolean(i));

                if (subItems.length === 1) {
                    return {
                        ...(subItems[0] as MenuSubItem),
                        count: item.count,
                    };
                }

                if (!subItems.length) {
                    return undefined;
                }

                return {
                    ...item,
                    subItems: subItems.length > 1 ? subItems : undefined,
                };
            })
            .filter((i): i is MenuItem => Boolean(i));
    }, [
        platoUser,
        t,
        canReadQuotes,
        canReadOrders,
        canManageTransactionDraft,
        taskCount,
        enableCustomerEventPlanning,
    ]);

    const activeSection = useMemo(() => {
        // Special case: /integrations/tapi routes should highlight Customers
        if (location.pathname.startsWith('/integrations/tapi')) {
            return NavPathOverrideEnum.CUSTOMERS;
        }

        for (const item of navigation) {
            if (item.subItems) {
                for (const subItem of item.subItems) {
                    if (location.pathname.startsWith(subItem.href)) {
                        return subItem.id;
                    }
                }
            }
        }

        for (const item of navigation) {
            if (item.href && location.pathname.startsWith(item.href)) {
                return item.id;
            }
        }

        return NavPathOverrideEnum.HOME;
    }, [navigation, location.pathname]);

    return { navigation, activeSection };
};
